import { Component, ElementRef, Input, type OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CountryConvertService } from '@odin/odin-core';

import { type BasketDetails, type BasketItem, type BasketDiscount, type BasketTax, type BasketDelivery, type BasketInvoice, type TransactionAddress, addressLines } from '../transactions-layout/models/transaction';

export class ReceiptDetail {
  constructor(
    public transactionName: string,
    public traceId: string,
    public custName: string,
    public detail: string,
    public time: Date,
    public authCode: string,
    public amount: number,
    public currencyCode: string,
    public transactionType?: string,
    public maskedCard?: string,
    public paymentReference?: string,
    public operatorName?: string,
    public amountRefunded?: number,
    public remainingValue?: number,
    public status?: string,
    public basket?: BasketDetails,
    public custPostcode?: string,
    public custEmail?: string,
    public deliveryAddress?: TransactionAddress,
    public billingAddress?: TransactionAddress,
  ) {
    this.getISOFromCode();
    
    // Sanitize basket arrays to remove any null values
    if (this.basket) {
      if (this.basket.basketItems) {
        this.basket.basketItems = this.basket.basketItems.filter(item => item !== null && item !== undefined);
      }
      if (this.basket.basketDiscounts) {
        this.basket.basketDiscounts = this.basket.basketDiscounts.filter(discount => discount !== null && discount !== undefined);
      }
      if (this.basket.basketTaxes) {
        this.basket.basketTaxes = this.basket.basketTaxes.filter(tax => tax !== null && tax !== undefined);
      }
    }
  }

  public getISOFromCode(): void {
    const newIso = new CountryConvertService().GetCountryByCurrencyCode(
      this.currencyCode,
    );
    if (newIso === undefined)
      this.currencyCode = 'GBP'; // default currency fallback
    else this.currencyCode = newIso.currencyCode;
  }

  public hasBasketData(): boolean {
    return !!(this.basket && (
      this.basket.basketItems?.length ||
      this.basket.basketDiscounts?.length ||
      this.basket.basketTaxes?.length ||
      this.basket.delivery
    ));
  }

  public get basketItems(): BasketItem[] {
    return this.basket?.basketItems || [];
  }

  public get basketDiscounts(): BasketDiscount[] {
    return this.basket?.basketDiscounts || [];
  }

  public get basketTaxes(): BasketTax[] {
    return this.basket?.basketTaxes || [];
  }

  public get delivery(): BasketDelivery | undefined {
    return this.basket?.delivery;
  }

  public get invoice(): BasketInvoice | undefined {
    return this.basket?.invoice;
  }

  public get deliveryAddressLines(): string[] {
    return addressLines(this.deliveryAddress);
  }

  public get billingAddressLines(): string[] {
    return addressLines(this.billingAddress);
  }

  /** Thor's line total already accounts for any line discount; fall back to qty x unit price */
  public lineTotal(item: BasketItem): number {
    return item.total ?? (item.unitPrice ?? 0) * (item.quantity || 1);
  }

  public get subtotal(): number {
    return this.basketItems.reduce((sum, item) => sum + this.lineTotal(item), 0);
  }

  /** true when any line, discount or delivery carries a tax amount */
  public get hasTaxDetail(): boolean {
    return this.basketItems.some((i) => i.taxAmount !== undefined && i.taxAmount !== null) ||
      this.basketDiscounts.some((d) => d.taxAmount !== undefined && d.taxAmount !== null) ||
      (this.delivery?.taxAmount !== undefined && this.delivery?.taxAmount !== null) ||
      this.basketTaxes.length > 0;
  }

  /** item tax + delivery tax + basket-level taxes - discount tax, matching Thor's TaxTotalAmount */
  public get taxTotal(): number {
    const items = this.basketItems.reduce((sum, i) => sum + (i.taxAmount ?? 0), 0);
    const discounts = this.basketDiscounts.reduce((sum, d) => sum + (d.taxAmount ?? 0), 0);
    const basketTaxes = this.basketTaxes.reduce((sum, t) => sum + (t.amount ?? 0), 0);
    return items + (this.delivery?.taxAmount ?? 0) + basketTaxes - discounts;
  }

  /** tax rolled up by code and rate across items, discounts (negative), delivery and basket-level taxes */
  public get taxSummary(): { label: string; amount: number }[] {
    const buckets = new Map<string, number>();
    const add = (code: string | undefined, rate: number | undefined, amount: number | undefined | null) => {
      if (amount === undefined || amount === null) return;
      const label = this.taxLabel(code, rate) || 'Tax';
      buckets.set(label, (buckets.get(label) ?? 0) + amount);
    };
    this.basketItems.forEach((i) => add(i.taxCode, i.taxRate, i.taxAmount));
    this.basketDiscounts.forEach((d) => add(d.taxCode, d.taxRate, d.taxAmount === undefined || d.taxAmount === null ? null : -d.taxAmount));
    if (this.delivery) add(this.delivery.taxCode, this.delivery.taxRate, this.delivery.taxAmount);
    this.basketTaxes.forEach((t) => add(t.code, t.rate, t.amount));
    return Array.from(buckets, ([label, amount]) => ({ label, amount: Math.round(amount * 100) / 100 }));
  }

  /** e.g. "VAT 20%", "20%", "VAT" or "" */
  public taxLabel(code?: string, rate?: number): string {
    const parts: string[] = [];
    if (code) parts.push(code);
    if (rate) parts.push(`${rate}%`);
    return parts.join(' ');
  }

  public get taxTypeLabel(): string {
    const taxType = this.basket?.taxType;
    switch (typeof taxType === 'string' ? taxType.toLowerCase() : taxType) {
      case 1:
      case 'exclusive':
        return 'Prices exclude tax';
      case 2:
      case 'exempt':
        return 'Tax exempt';
      default:
        return 'Prices include tax';
    }
  }
}

@Component({
  standalone: false,
  selector: 'odin-print-receipt',
  templateUrl: './print-receipt.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./print-receipt.component.css'],
})
export class PrintReceiptComponent implements OnDestroy {
  @Input() public receiptDetail?: ReceiptDetail = undefined;

  private printEl: HTMLElement | null = null;

  // Reparent the print template to <body>. Inside the app shell it sits under
  // positioned/transformed ancestors, and iOS Safari then prints it at the
  // phone's viewport width in the top-left of the page; at body level the
  // print CSS can position it absolutely against the full page box.
  @ViewChild('printTemplate')
  set printTemplate(el: ElementRef<HTMLElement> | undefined) {
    this.printEl = el?.nativeElement ?? null;
    if (this.printEl && this.printEl.parentElement !== document.body) {
      document.body.appendChild(this.printEl);
    }
  }

  ngOnDestroy(): void {
    this.printEl?.remove();
  }
}

